(function () {
  const ROOT_SELECTOR = '[data-product-item-image-slider-enabled="true"]';
  const SLIDER_SELECTOR = '[data-product-item-image-slider]';
  const PREV_SELECTOR = '[data-product-item-slider-prev]';
  const NEXT_SELECTOR = '[data-product-item-slider-next]';
  const OUTER_SWIPER_SELECTOR =
    '.featured-collection-slider__content.carousel.swiper, .recommended-products__content.carousel.swiper, .recently-viewed-products__content.carousel.swiper';

  const instances = new Map();
  const outerSwipeRetryMap = new WeakMap();
  const outerInterceptors = new WeakSet();
  const MAX_OUTER_RETRIES = 30;

  function getElements(slider) {
    const wrapper = slider.querySelector('.product-item__image-slider-wrapper');
    const slides = Array.from(slider.querySelectorAll('.product-item__image-slide'));
    const media = slider.closest('.product-item__media');
    const prevButton = media ? media.querySelector(PREV_SELECTOR) : null;
    const nextButton = media ? media.querySelector(NEXT_SELECTOR) : null;

    return { wrapper, slides, prevButton, nextButton };
  }

  function initSlider(slider) {
    if (!slider || instances.has(slider)) return;

    const { wrapper, slides, prevButton, nextButton } = getElements(slider);
    if (!wrapper || slides.length < 2) return;

    let index = 0;
    let pointerActive = false;
    let isHorizontalGesture = false;
    let hasDragged = false;
    let startX = 0;
    let startY = 0;
    let deltaX = 0;
    let deltaY = 0;
    let dots = [];
    const canSwipe = true;

    function clampIndex(value) {
      if (value < 0) return 0;
      if (value > slides.length - 1) return slides.length - 1;
      return value;
    }

    function updateButtons() {
      if (prevButton) prevButton.disabled = index === 0;
      if (nextButton) nextButton.disabled = index === slides.length - 1;
    }

    function update() {
      wrapper.style.transform = 'translate3d(' + index * -100 + '%, 0, 0)';
      updateButtons();
      updateDots();
    }

    function goTo(nextIndex) {
      index = clampIndex(nextIndex);
      update();
    }

    function updateDots() {
      if (!dots.length) return;
      dots.forEach((dot, dotIndex) => {
        dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false');
      });
    }

    function buildDots() {
      const dotsWrap = document.createElement('div');
      dotsWrap.className = 'product-item__image-slider-dots';
      dotsWrap.setAttribute('aria-label', 'Product image navigation');

      slides.forEach((_, dotIndex) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'product-item__image-slider-dot';
        dot.setAttribute('aria-label', 'Go to image ' + (dotIndex + 1));
        dot.addEventListener('click', event => {
          event.preventDefault();
          event.stopPropagation();
          goTo(dotIndex);
        });
        dotsWrap.appendChild(dot);
        dots.push(dot);
      });

      slider.appendChild(dotsWrap);
    }

    function onPrevClick(event) {
      event.preventDefault();
      event.stopPropagation();
      goTo(index - 1);
    }

    function onNextClick(event) {
      event.preventDefault();
      event.stopPropagation();
      goTo(index + 1);
    }

    function onPointerDown(event) {
      pointerActive = true;
      isHorizontalGesture = false;
      hasDragged = false;
      startX = event.clientX;
      startY = event.clientY;
      deltaX = 0;
      deltaY = 0;
      wrapper.style.transition = 'none';
    }

    function onPointerMove(event) {
      if (!pointerActive) return;
      deltaX = event.clientX - startX;
      deltaY = event.clientY - startY;

      if (!isHorizontalGesture) {
        const horizontalIntent = Math.abs(deltaX) > Math.abs(deltaY) + 4;
        const movedEnough = Math.abs(deltaX) > 6;
        if (horizontalIntent && movedEnough) {
          isHorizontalGesture = true;
        }
      }

      if (isHorizontalGesture) {
        hasDragged = true;
        event.preventDefault();
        event.stopPropagation();
      }
    }

    function onPointerEnd(event) {
      if (!pointerActive) return;
      pointerActive = false;
      if (isHorizontalGesture && event) {
        event.preventDefault();
        event.stopPropagation();
      }
      wrapper.style.transition = '';

      const threshold = 32;
      if (isHorizontalGesture && deltaX <= -threshold) {
        goTo(index + 1);
      } else if (isHorizontalGesture && deltaX >= threshold) {
        goTo(index - 1);
      } else {
        update();
      }
    }

    function onSliderClick(event) {
      if (!hasDragged) return;
      event.preventDefault();
      event.stopPropagation();
      hasDragged = false;
    }

    function onKeydown(event) {
      if (event.key === 'ArrowLeft') {
        goTo(index - 1);
      } else if (event.key === 'ArrowRight') {
        goTo(index + 1);
      }
    }

    if (prevButton) prevButton.addEventListener('click', onPrevClick);
    if (nextButton) nextButton.addEventListener('click', onNextClick);

    if (canSwipe) {
      slider.addEventListener('pointerdown', onPointerDown, { passive: true, capture: true });
      slider.addEventListener('pointermove', onPointerMove, { passive: false, capture: true });
      slider.addEventListener('pointerup', onPointerEnd, { passive: false, capture: true });
      slider.addEventListener('pointercancel', onPointerEnd, { passive: false, capture: true });
      slider.addEventListener('pointerleave', onPointerEnd, { passive: false, capture: true });
    }
    slider.addEventListener('click', onSliderClick, true);
    slider.addEventListener('keydown', onKeydown);

    slider.setAttribute('tabindex', '0');
    slider.classList.add('is-product-item-slider-ready');
    buildDots();

    update();

    instances.set(slider, {
      destroy() {
        if (prevButton) prevButton.removeEventListener('click', onPrevClick);
        if (nextButton) nextButton.removeEventListener('click', onNextClick);

        if (canSwipe) {
          slider.removeEventListener('pointerdown', onPointerDown);
          slider.removeEventListener('pointermove', onPointerMove);
          slider.removeEventListener('pointerup', onPointerEnd);
          slider.removeEventListener('pointercancel', onPointerEnd);
          slider.removeEventListener('pointerleave', onPointerEnd);
        }
        slider.removeEventListener('click', onSliderClick, true);
        slider.removeEventListener('keydown', onKeydown);

        slider.classList.remove('is-product-item-slider-ready');
        wrapper.style.transform = '';
        const dotsWrap = slider.querySelector('.product-item__image-slider-dots');
        if (dotsWrap) dotsWrap.remove();
        dots = [];
      }
    });
  }

  function destroySlider(slider) {
    const instance = instances.get(slider);
    if (!instance) return;
    instance.destroy();
    instances.delete(slider);
  }

  function disableOuterCarouselSwipe(swiperContainer) {
    if (!(swiperContainer instanceof Element)) return;

    const swiperInstance = swiperContainer.swiper;
    if (!swiperInstance) {
      queueOuterSwipeRetry(swiperContainer);
      return;
    }

    swiperInstance.allowTouchMove = false;
    if (swiperInstance.params) {
      swiperInstance.params.allowTouchMove = false;
      swiperInstance.params.simulateTouch = false;
      swiperInstance.params.touchRatio = 0;
      swiperInstance.params.grabCursor = false;
    }
    swiperInstance.update();

    swiperContainer.style.touchAction = 'pan-y';
    attachOuterInterceptors(swiperContainer);
  }

  function queueOuterSwipeRetry(swiperContainer) {
    const retryCount = outerSwipeRetryMap.get(swiperContainer) || 0;
    if (retryCount >= MAX_OUTER_RETRIES) return;

    outerSwipeRetryMap.set(swiperContainer, retryCount + 1);
    window.setTimeout(() => {
      disableOuterCarouselSwipe(swiperContainer);
    }, 120);
  }

  function disableOuterSwipeInRoot(root) {
    if (!(root instanceof Element)) return;

    if (root.matches(OUTER_SWIPER_SELECTOR)) {
      disableOuterCarouselSwipe(root);
    }

    root.querySelectorAll(OUTER_SWIPER_SELECTOR).forEach(disableOuterCarouselSwipe);
  }

  function attachOuterInterceptors(swiperContainer) {
    if (!(swiperContainer instanceof Element)) return;
    if (outerInterceptors.has(swiperContainer)) return;

    const enforce = () => {
      disableOuterCarouselSwipe(swiperContainer);
    };

    swiperContainer.addEventListener('touchstart', enforce, { passive: true });
    swiperContainer.addEventListener('pointerdown', enforce, { passive: true });
    swiperContainer.addEventListener('mouseenter', enforce, { passive: true });
    outerInterceptors.add(swiperContainer);
  }

  function initInRoot(root) {
    if (!(root instanceof Element)) return;

    if (root.matches(SLIDER_SELECTOR)) {
      initSlider(root);
    }

    root.querySelectorAll(SLIDER_SELECTOR).forEach(initSlider);
    disableOuterSwipeInRoot(root);
  }

  function destroyInRoot(root) {
    if (!(root instanceof Element)) return;

    if (root.matches(SLIDER_SELECTOR)) {
      destroySlider(root);
    }

    root.querySelectorAll(SLIDER_SELECTOR).forEach(destroySlider);
  }

  function boot() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(initInRoot);
  }

  const mutationObserver = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => {
        if (!(node instanceof Element)) return;

        if (node.matches(ROOT_SELECTOR)) {
          initInRoot(node);
          return;
        }

        if (node.matches(SLIDER_SELECTOR)) {
          initSlider(node);
        }

        node.querySelectorAll(SLIDER_SELECTOR).forEach(initSlider);
        disableOuterSwipeInRoot(node);
      });

      mutation.removedNodes.forEach(node => {
        if (!(node instanceof Element)) return;
        destroyInRoot(node);
      });
    });
  });

  document.addEventListener('shopify:section:load', event => {
    initInRoot(event.target);
  });

  document.addEventListener('shopify:section:unload', event => {
    destroyInRoot(event.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  mutationObserver.observe(document.body, {
    childList: true,
    subtree: true
  });
})();
